/**
 * TestingGroupHeader — the live path heading a section, on the same grid columns
 * as its children (a path is tested and rotated like anything else). What belongs
 * to the GROUP rather than to a column — element count, cap, pending changes —
 * rides inline after the name, so a section costs one row, not two.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type {
  DemoteDestination,
  QueueElement,
  QueuePoolSummary,
} from '@trafficpulse/shared';

import type { QueueProvider } from '@/features/trackers/flow-optimizer/queue-provider';
import QueueTimelineCell from '../../../timeline/QueueTimelineCell';
import DemoteCell from '../../../live/DemoteCell';
import SelectCheckbox from '../../../bulk/row/SelectCheckbox';
import type { BulkAwareProps } from '../../../bulk/row/bulk-row.types';
import { Cell, COLUMN_LABELS, GridRow } from '../testing-grid';
import NextRunCell, { rowTone, type NextRunCellProps } from './NextRunCell';
import { CapMeter, ElementIdentity, RunsBadge, TestedFor } from './testing-row-bits';
import { PathStatusBadge } from '../../../status';

export interface TestingGroupHeaderProps
  extends NextRunCellProps,
    BulkAwareProps {
  path: QueueElement;
  pool?: QueuePoolSummary;
  itemCount: number;
  /** Elements in this section the next run would rotate out. */
  changeCount: number;
  open: boolean;
  onToggle: () => void;
  campaignId: string;
  provider: QueueProvider;
  /** Move this live path off the tracker; paths only, hence header-level. */
  onDemote: (element: QueueElement, to: DemoteDestination) => void;
  demotePending: boolean;
}

const TestingGroupHeader: FC<TestingGroupHeaderProps> = ({
  path,
  pool,
  itemCount,
  changeCount,
  open,
  onToggle,
  swap,
  held,
  campaignId,
  provider,
  onDemote,
  demotePending,
  bulk,
}) => {
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <GridRow tone={rowTone({ swap, held })} className="bg-muted/30">
      <Cell>
        {bulk ? (
          <SelectCheckbox
            element={path}
            checked={bulk.isSelected(path)}
            onToggle={bulk.onToggleSelect}
            disabled={!bulk.selectable}
          status={bulk.statusOf?.(path)}
          />
        ) : null}
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-label={`${open ? 'Collapse' : 'Expand'} ${path.itemName}`}
          className="-ml-1 flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <Chevron className="h-3.5 w-3.5" aria-hidden />
        </button>
        <ElementIdentity el={path} strong />
        {/* Same chip as the Paths table — the All view must not be the one
            place a switched-off path looks healthy. */}
        <PathStatusBadge element={path} />
        <RunsBadge el={path} />
        <span className="flex shrink-0 items-center gap-1.5 text-[10px] text-muted-foreground">
          <span className="tabular-nums" title={`${itemCount} element(s) under this path`}>
            {itemCount} item{itemCount === 1 ? '' : 's'}
          </span>
          {pool?.cap != null ? (
            <>
              <span aria-hidden>·</span>
              <CapMeter pool={pool} />
            </>
          ) : null}
          {changeCount > 0 ? (
            <>
              <span aria-hidden>·</span>
              <span
                className="font-semibold tabular-nums text-primary"
                title="Rows in this section the next run rotates out (the path itself counts)"
              >
                {changeCount} leaving
              </span>
            </>
          ) : null}
        </span>
      </Cell>
      <Cell label={COLUMN_LABELS.underTest}>
        <TestedFor el={path} />
      </Cell>
      <Cell label={COLUMN_LABELS.nextRun}>
        <NextRunCell swap={swap} held={held} />
      </Cell>
      <Cell label={COLUMN_LABELS.actions} align="right">
        <DemoteCell
          element={path}
          campaignId={campaignId}
          provider={provider}
          onDemote={onDemote}
          pending={demotePending}
        />
      </Cell>
      <Cell align="right">
        <QueueTimelineCell element={path} campaignId={campaignId} provider={provider} />
      </Cell>
    </GridRow>
  );
};

export default TestingGroupHeader;
